import request from '@/modules/base/util/request'
import store from '@/redux/store'

import config from '@/modules/banking/config'
import { BankingService, BookingService } from '@/modules/banking/services'
import { FINANCE_API } from '@/modules/banking/constants'
import { BankingTransactionRequestError, CancelPinRequestError, CancelTanRequestError, TransactionCanceled } from '@/modules/banking/errors'
import { openPinModal, openTanModal, openChangeTanModal, openUniversalModal, openValidationModal, silentContactChange, silentPinUpdate } from '@/modules/banking/_helpers'
import { closeBankAccountSetupModal, deactivateShowBankaccountSetupTanUniversal, setBankAccountSetupTransaction } from '@/redux/ducks/banking/bankAccountSetup'
import openRedirectModal from '@/modules/banking/_helpers/openRedirectModal'
import { generateProcessId, getProcessIdHeader } from '@/modules/banking/_helpers/generateProcessId'

const { urls } = config
const { TRANSACTION_STATE } = FINANCE_API

// The backend answers a state request within milliseconds, so the polling loop has to pace
// itself: without this wait every unchanged QUEUED/RUNNING state would fire the next request
// immediately and flood the backend.
const POLLING_DELAY_MS = 2000

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const systems = {
  bankingApi: 'bankingApi',
  bookingApi: 'bookingApi',
  bookingApiSetup: 'bookingApiSetup',
}

export const pollTransaction = (transaction, system = systems.bookingApi, processId = null) => {
  if (!processId) {
    processId = generateProcessId()
  }
  const { transactionId, transactionState, token, meta, links } = transaction

  let pollingRequest
  switch (system) {
    case systems.bankingApi:
      pollingRequest = BankingService.psd2.contactSetup.getState(token, processId)
      break
    case systems.bookingApiSetup:
      const link = links.find(link => link.rel === 'setupstate')
      pollingRequest = request(`${urls.bookingProxy}${link.href}`, {
        method: link.method,
        headers: {
          ...getProcessIdHeader(processId),
          authToken: token,
        },
        auth: true,
      })
      break

    default:
      if (!transactionId || !transactionState) {
        // there is noting to do
        return {}
      }
      pollingRequest = request(`${urls.fetchTransaction}/${transactionId}/${transactionState}`, { auth: true, headers: { ...getProcessIdHeader(processId) } })
      break
  }

  return new Promise((resolve, reject) => {
    pollingRequest
      .then(({ body }) => {
        const data = ['bankingApi', 'bookingApiSetup'].includes(system) ? body : body.data
        const { transactionState } = data

        const onPinModalAbort = (data) => {
          reject(new CancelPinRequestError(data))
          store.dispatch(closeBankAccountSetupModal())
        }

        switch (transactionState) {
          case TRANSACTION_STATE.QUEUED:
          case TRANSACTION_STATE.RUNNING:
            // nothing changed yet, keep polling - but not before POLLING_DELAY_MS has passed
            return wait(POLLING_DELAY_MS).then(() => data)
          case TRANSACTION_STATE.SUCCEEDED:
            if (!data.provideUniversalRequest) {
              store.dispatch(deactivateShowBankaccountSetupTanUniversal())
            }
            store.dispatch(setBankAccountSetupTransaction(data))
            resolve({ ...data, nextToken: token })
            return { ...data, nextToken: token }
          case TRANSACTION_STATE.CANCELED:
            return reject(new TransactionCanceled(data))
          case TRANSACTION_STATE.WAITING_FOR_TAN_CALLBACK:
            return openTanModal({
              transaction: {
                ...data,
                transactionId: transaction.transactionId,
              },
              processId,
              onSuccess: () => { },
              onError: (error) => reject(error),
              onAbort: () => reject(new CancelTanRequestError(data)),
            })
          case TRANSACTION_STATE.WAITING_FOR_PIN_CALLBACK: {
            if (meta?.silent === true) {
              return silentPinUpdate(data, meta, processId)
            } else {
              return openPinModal({
                transaction: {
                  ...data,
                  transactionId: transaction.transactionId,
                },
                processId,
                onSuccess: () => { },
                onError: (error) => reject(error),
                onAbort: onPinModalAbort,
              })
            }
          }
          case TRANSACTION_STATE.WAITING_FOR_UNIVERSAL_CALLBACK: {
            return openUniversalModal({
              transaction: {
                ...data,
                transactionId: transaction.transactionId,
              },
              onSuccess: () => { },
              onError: (error) => reject(error),
              onAbort: () => reject(new TransactionCanceled(data)),
            })
          }
          case TRANSACTION_STATE.WAITING_FOR_REDIRECT_CALLBACK: {
            return openRedirectModal({
              transaction: {
                ...data,
                transactionId: transaction.transactionId,
              },
              onSuccess: () => { },
              onError: (error) => reject(error),
              onAbort: () => reject(new TransactionCanceled(data)),
            })
          }
          case TRANSACTION_STATE.WAITING_FOR_VALIDATION_CALLBACK: {
            return openValidationModal({
              transaction: {
                ...data,
                transactionId: transaction.transactionId,
              },
              onSuccess: () => { },
              onError: (error) => reject(error),
              onAbort: () => reject(new TransactionCanceled(data)),
            })
          }
          case TRANSACTION_STATE.WAITING_FOR_CONTACT_CHANGE_CALLBACK: {
            if (meta?.silent === true) {
              return silentContactChange(data, meta, processId)
            } else {
              return openChangeTanModal({
                transaction: {
                  ...data,
                  transactionId: transaction.transactionId,
                  meta,
                },
                processId,
                onSuccess: () => { },
                onError: (error) => reject(error),
                onAbort: () => reject(new TransactionCanceled(data)),
              })
            }
          }
          default:
            return reject(data)
        }
      })
      .then((result) => {
        const { transactionId, transactionState } = result
        if (transactionState === TRANSACTION_STATE.SUCCEEDED) return result

        if (transactionId) {
          return pollTransaction({ transactionId, transactionState }, systems.bookingApi, processId)
        }

        return pollTransaction({ token }, 'bankingApi', processId)
      })
      .then((result) => {
        resolve(result)
      })
      .catch((error) => {
        // handle BankingTransactionRequestError
        let transactionId
        if (error instanceof BankingTransactionRequestError) {
          transactionId = error.transaction?.transactionId
        } else {
          transactionId = error.transactionId
        }

        if (!transactionId) return reject(error)

        BookingService.transaction.delete(transactionId, processId)
          .then(() => reject(error))
          .catch(() => reject(error))
      })
  })
}

export default pollTransaction
